import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useToast, toastErrorMessage } from "../lib/toast";
import Sheet from "./Sheet";
import { TASK_TYPES } from "../lib/taskTypes";
import TimePicker from "./TimePicker";

/**
 * Modal to edit an existing task.
 * Props:
 *   task  - full task object (must include id, company_id, summary, due_date, due_time, assigned_to_id, contact_id, task_type)
 *   users - [{id, name}] for the assignee picker (optional; modal will fetch if omitted)
 *   onClose, onSaved
 */
export default function TaskEditModal({ task, users: usersProp, onClose, onSaved }) {
  const toast = useToast();
  const [users, setUsers] = useState(usersProp || []);
  const [contacts, setContacts] = useState([]);
  const [f, setF] = useState({
    summary: task?.summary || "",
    task_type: task?.task_type || "Phone Call",
    due_date: task?.due_date || "",
    due_time: task?.due_time || "",
    assigned_to_id: task?.assigned_to_id || "",
    contact_id: task?.contact_id || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!usersProp || usersProp.length === 0) {
      api.get("/users").then(r => setUsers(r.data)).catch(()=>{});
    }
    if (task?.company_id) {
      api.get(`/companies/${task.company_id}/contacts`)
         .then(r => setContacts(r.data)).catch(()=>{});
    }
  }, [task?.company_id]);  // eslint-disable-line

  async function save() {
    setErr("");
    if (!f.summary.trim() || !f.due_date) { setErr("Summary and due date are required"); return; }
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}`, {
        summary: f.summary.trim(),
        task_type: f.task_type,
        due_date: f.due_date,
        due_time: f.due_time || null,
        assigned_to_id: f.assigned_to_id || null,
        contact_id: f.contact_id || null,
      });
      toast.success("Task updated");
      onSaved?.();
      onClose();
    } catch(e) {
      const m = toastErrorMessage(e); setErr(m); toast.error(m);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open onClose={onClose} title="Edit task"
      footer={
        <div className="flex gap-2 justify-end">
          <button className="cesco-btn-ghost" onClick={onClose} data-testid="task-edit-cancel">Cancel</button>
          <button className="cesco-btn-primary" onClick={save} disabled={saving}
                  data-testid="task-edit-save">{saving ? "Saving…" : "Save"}</button>
        </div>}>
      <div className="space-y-3">
        <div>
          <label className="cesco-label">Type *</label>
          <select className="cesco-input" value={f.task_type}
                  onChange={e=>setF({...f, task_type:e.target.value})} data-testid="task-edit-type">
            {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="cesco-label">Summary *</label>
          <textarea className="cesco-input" rows={3} value={f.summary}
                    onChange={e=>setF({...f, summary:e.target.value})} data-testid="task-edit-summary"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="cesco-label">Due date *</label>
            <input className="cesco-input" type="date" value={f.due_date}
                   onChange={e=>setF({...f, due_date:e.target.value})} data-testid="task-edit-due"/>
          </div>
          <div>
            <label className="cesco-label">Due time</label>
            <TimePicker value={f.due_time || ""}
                        onChange={v => setF({...f, due_time: v})}
                        testId="task-edit-time"/>
          </div>
        </div>
        {contacts.length > 0 && (
          <div>
            <label className="cesco-label">Related contact</label>
            <select className="cesco-input" value={f.contact_id || ""}
                    onChange={e=>setF({...f, contact_id:e.target.value})} data-testid="task-edit-contact">
              <option value="">- (company-level task)</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name}{c.position ? ` · ${c.position}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        {users.length > 0 && (
          <div>
            <label className="cesco-label">Assign to</label>
            <select className="cesco-input" value={f.assigned_to_id || ""}
                    onChange={e=>setF({...f, assigned_to_id:e.target.value})} data-testid="task-edit-assignee">
              <option value="">(unassigned)</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
        {err && <div className="text-sm text-red-600" data-testid="task-edit-error">{err}</div>}
      </div>
    </Sheet>
  );
}
